import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FileType, MeetingType, Sentiment } from '@alucio/aws-beacon-amplify/src/models';
import {
  DNAButton,
  DNAButtonProps,
  DNAConditionalWrapper,
  DNAContextMenu,
  DNAText,
} from '@alucio/lux-ui';
import { DNAPopover } from 'src/components/DNA';
import { isDocumentVersionORM, isFolderItemORM } from 'src/types/typeguards';
import { Tab } from 'src/screens/Meetings/PresentationControls/PresentationControls';
import { useMeetingsState } from 'src/state/context/Meetings/MeetingsStateProvider';
import { useContent } from 'src/state/context/ContentProvider/ContentProvider';
import { useHighlighter } from 'src/components/Highlighter/HighlighterProvider';
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';
import PresentationNav from 'src/screens/Meetings/SharedComponents/PresentationNav';
import OpenedPresentations from
  'src/screens/Meetings/PresentationControls/TabContent/OpenedPresentations/OpenedPresentations';
import { CanvasVariantEnum } from 'src/components/Highlighter/UseCanvas';
import { getPresentationVirtualStatus } from '../../util';
import * as logger from 'src/utils/logger';

const HTML_MULTI_PAGE_DEBUG_PREFIX = '[HTML_MP_DEBUG]';

export const useVirtualnActionBarComponents = () => {
  const {
    meetingORM,
    setCurrentTab,
    endMeeting,
    setPresentationControlsVisible,
    slideRollVisible,
    toggleSlideRollVisibility,
    setSlideRollVisible,
    textSearchVisible,
    toggleTextSearchVisibility,
    canEndMeeting,
    notesPanelVisible,
    toggleAllNotes,
    toggleMeetingType,
    updateReaction,
    updateFollowUp,
    presentedMeta,
    focusPopoutWindow,
  } = useMeetingsState();

  const featureFlags = useFeatureFlags(
    'enableMeetingNotes',
    'enableMeetingReactions',
    'enableMeetingFollowUp',
  );

  const { presentations, activePresentation, isPlayerReady } = useContent();
  const { orm } = { ...activePresentation?.presentable };
  const [showPresentations, setShowPresentations] = useState<boolean>(false);
  const presentationVirtualStatus = useMemo(() => getPresentationVirtualStatus(orm), [orm]);
  const isVirtual = meetingORM?.model.type === MeetingType.VIRTUAL;

  useEffect(() => {
    logger.contentPresenting.highlighterPlayer.debug('Detect document changed, set highlighter visibility to false.')
    setHighlighterVisible(false)
  }, [activePresentation?.presentable.orm.model.id])

  const buttonDefaults: DNAButtonProps = {
    appearance: 'ghost',
    status: 'subtle',
    colorMode: 'dark',
  };

  const currentORM = isDocumentVersionORM(orm)
    ? orm
    : isFolderItemORM(orm)
      ? orm.relations.itemORM
      : undefined

  const isWebVideoHTMLDoc = isDocumentVersionORM(currentORM) && (
    currentORM.model.type === FileType.WEB ||
    currentORM.model.type === FileType.MP4 ||
    (currentORM.model.type === FileType.HTML && !currentORM.meta.isMultiPageHTML)
  );
  const isMultiPageHTMLDoc = isDocumentVersionORM(currentORM) &&
    currentORM.model.type === FileType.HTML &&
    !!currentORM.meta.isMultiPageHTML;

  const { setHighlighterMode, selectedType, setHighlighterVisible } = useHighlighter();

  const openSidePanel = (tab?: Tab) => {
    setSlideRollVisible(false);
    setPresentationControlsVisible(true);
    if (tab) setCurrentTab(tab)
  };

  const toggleShowPresentations = () => {
    setShowPresentations((showPresentations) => !showPresentations)
  };

  const toggleMeetingTypeTooltipMessage =
    !isPlayerReady
      ? `${isVirtual ? 'Exit' : ''} Presenter View ${!isVirtual ? 'available' : ''} once document is loaded`
      : (presentationVirtualStatus.isHTMLWebPresentation && presentationVirtualStatus.message) || 'Exit Presenter View';
  const presentationNav = !isWebVideoHTMLDoc && !!presentations.length && <PresentationNav />;

  logger.contentPresenting.contentProvider.debug(`${HTML_MULTI_PAGE_DEBUG_PREFIX} Virtual action bar nav visibility`, {
    documentType: isDocumentVersionORM(currentORM) ? currentORM.model.type : undefined,
    isMultiPageHTML: isDocumentVersionORM(currentORM) ? currentORM.meta.isMultiPageHTML : undefined,
    isWebVideoHTMLDoc,
    presentationsLength: presentations.length,
    hasActivePresentation: !!activePresentation,
    shouldShowPresentationNav: !isWebVideoHTMLDoc && !!presentations.length,
    isMainPlayer: true,
  });

  const onMyContentButtonPressed = useCallback(() => {
    openSidePanel('BROWSE_CONTENT')
  }, [openSidePanel])

  const myContentButton = (
    <DNAButton
      {...buttonDefaults}
      testID="my-content-button"
      onPress={onMyContentButtonPressed}
    >
      My Content
    </DNAButton>
  );

  const presentationsButton = !!presentations.length && (
    <DNAPopover
      lazyMount
      placement="top"
      interactive={true}
      visible={showPresentations}
      onBackdropPress={toggleShowPresentations}
      type="menu"
      offset={30}
    >
      <DNAPopover.Anchor>
        <DNAButton
          {...buttonDefaults}
          testID="files-button"
          appearance={showPresentations ? 'filled' : 'ghost'}
          status={showPresentations ? 'primary' : 'subtle'}
          onPress={toggleShowPresentations}
        >
          Opened Files
        </DNAButton>
      </DNAPopover.Anchor>
      <DNAPopover.Content>
        <OpenedPresentations
          variant="actionBar"
          onClose={toggleShowPresentations}
        />
      </DNAPopover.Content>
    </DNAPopover>
  );

  const slidesButton = !!activePresentation && !isWebVideoHTMLDoc && !!presentations.length && (
    <DNAButton
      {...buttonDefaults}
      testID="in-person-meeting-slides-btn"
      appearance={slideRollVisible ? 'filled' : 'ghost'}
      status={slideRollVisible ? 'primary' : 'subtle'}
      onPress={toggleSlideRollVisibility}
    >
      Slides
    </DNAButton>
  );

  const meetingDetailsButton = (
    <DNAContextMenu placement="top" style={{ bottom: 8 }}>
      <DNAContextMenu.Anchor>
        <DNAButton
          {...buttonDefaults}
          iconLeft="dots-three-vertical-bold"
        />
      </DNAContextMenu.Anchor>
      <DNAContextMenu.Items>
        <DNAContextMenu.Item
          icon="calendar-dots"
          title="Meeting Details"
          onPress={() => openSidePanel('MEETING_DETAILS')}
        />
      </DNAContextMenu.Items>
    </DNAContextMenu>
  )

  const highlighterContextMenu = !!activePresentation && !isMultiPageHTMLDoc && (
    <DNAConditionalWrapper
      condition={!selectedType}
      wrapper={children => (
        <DNAPopover
          placement="top"
          type="tooltip"
          offset={30}
          disablePopover={['tabletPWA']}
        >
          <DNAPopover.Anchor>
            <DNAContextMenu placement="top" style={{ bottom: 8 }}>
              <DNAContextMenu.Anchor>
                {children}
              </DNAContextMenu.Anchor>
              <DNAContextMenu.Items>
                <DNAContextMenu.Item
                  icon="arrow-up-right"
                  title="Arrow"
                  onPress={() => setHighlighterMode(CanvasVariantEnum.arrow)}
                  testID="arrow-btn"
                />
                <DNAContextMenu.Item
                  icon="square-fill"
                  title="Box highlighter"
                  onPress={() => setHighlighterMode(CanvasVariantEnum.square)}
                  testID="box-btn"
                />
              </DNAContextMenu.Items>
            </DNAContextMenu>
          </DNAPopover.Anchor>
          <DNAPopover.Content>
            <DNAText colorMode="dark">Highlighter</DNAText>
          </DNAPopover.Content>
        </DNAPopover>
      )}
    >
      <DNAButton
        {...buttonDefaults}
        key="Marker"
        testID="toggle-marker-button"
        appearance={selectedType ? 'filled' : 'ghost'}
        status={selectedType ? 'primary' : 'subtle'}
        iconLeft="highlighter"
        onPress={() => {
          if (selectedType) setHighlighterVisible(false)
        }}
      />
    </DNAConditionalWrapper>
  );

  const textSearchButton = activePresentation && !isWebVideoHTMLDoc && !!presentations.length && (
    <DNAPopover
      placement="top"
      type="tooltip"
      offset={30}
      disablePopover={['tabletPWA']}
    >
      <DNAPopover.Anchor>
        <DNAButton
          {...buttonDefaults}
          testID="in-person-meeting-search-in-document-btn"
          appearance={textSearchVisible ? 'filled' : 'ghost'}
          status={textSearchVisible ? 'primary' : 'subtle'}
          iconLeft="magnifying-glass"
          onPress={toggleTextSearchVisibility}
        />
      </DNAPopover.Anchor>
      <DNAPopover.Content>
        <DNAText colorMode="dark">Search</DNAText>
      </DNAPopover.Content>
    </DNAPopover>
  );

  const sentimentsContextMenu = !!activePresentation &&
    (featureFlags.enableMeetingReactions || featureFlags.enableMeetingFollowUp) ? (
      <DNAPopover
        placement="top"
        type="tooltip"
        offset={30}
        disablePopover={['tabletPWA']}
      >
        <DNAPopover.Anchor>
          <DNAContextMenu placement="top" style={{ bottom: 8 }}>
            <DNAContextMenu.Anchor>
              <DNAButton
                {...buttonDefaults}
                appearance={(presentedMeta?.sentiment || presentedMeta?.followUp) ? 'filled' : 'ghost'}
                status={(presentedMeta?.sentiment || presentedMeta?.followUp) ? 'primary' : 'subtle'}
                iconLeft="smiley"
              />
            </DNAContextMenu.Anchor>
            <DNAContextMenu.Items>
              {featureFlags.enableMeetingReactions &&
                <DNAContextMenu.Item
                  icon={presentedMeta?.sentiment === Sentiment.POSITIVE ? 'caret-up-fill' : 'caret-up'}
                  iconStatus={presentedMeta?.sentiment === Sentiment.POSITIVE ? 'success' : 'tertiary'}
                  title="Positive"
                  onPress={() => updateReaction(Sentiment.POSITIVE)}
                  testID="positive-reaction-btn"
                />
              }
              {featureFlags.enableMeetingReactions &&
                <DNAContextMenu.Item
                  icon={presentedMeta?.sentiment === Sentiment.NEGATIVE ? 'caret-down-fill' : 'caret-down'}
                  iconStatus={presentedMeta?.sentiment === Sentiment.NEGATIVE ? 'warning' : 'tertiary'}
                  title="Negative"
                  onPress={() => updateReaction(Sentiment.NEGATIVE)}
                  testID="negative-reaction-btn"
                />
              }
              {featureFlags.enableMeetingFollowUp &&
                <DNAContextMenu.Item
                  icon={presentedMeta?.followUp ? 'flag-fill' : 'flag'}
                  iconStatus={presentedMeta?.followUp ? 'primary' : 'tertiary'}
                  title="Flag for follow-up"
                  onPress={updateFollowUp}
                  testID="follow-up-flag-btn"
                />
              }
            </DNAContextMenu.Items>
          </DNAContextMenu>
        </DNAPopover.Anchor>
        <DNAPopover.Content>
          <DNAText colorMode="dark">Sentiments</DNAText>
        </DNAPopover.Content>
      </DNAPopover>
    ) : null;

  const myNotesButton = !!activePresentation && !!presentations.length ? (
    <DNAPopover
      placement="top"
      type="tooltip"
      offset={30}
      disablePopover={['tabletPWA']}
    >
      <DNAPopover.Anchor>
        <DNAButton
          {...buttonDefaults}
          key="My Notes"
          testID="toggle-my-notes-button"
          appearance={notesPanelVisible ? 'filled' : 'ghost'}
          status={notesPanelVisible ? 'primary' : 'subtle'}
          iconLeft="note-pencil"
          onPress={toggleAllNotes}
        />
      </DNAPopover.Anchor>
      <DNAPopover.Content>
        <DNAText colorMode="dark">My Notes</DNAText>
      </DNAPopover.Content>
    </DNAPopover>
  ) : null;

  const endMeetingButton = (
    <DNAButton
      {...buttonDefaults}
      testID="in-person-meeting-end-meeting-btn"
      appearance="filled"
      status="danger"
      onPress={endMeeting}
      disabled={!canEndMeeting}
      children="End meeting"
    />
  );

  const changeToVirtualModeButton = (
    <DNAPopover
      placement="top"
      type="tooltip"
      offset={30}
    >
      <DNAPopover.Anchor>
        <DNAButton
          {...buttonDefaults}
          testID="change-meeting-mode-button"
          iconRight={isVirtual ? 'lectern' : 'presentation-chart'}
          onPress={toggleMeetingType}
          disabled={presentationVirtualStatus.isHTMLWebPresentation || !isPlayerReady}
        />
      </DNAPopover.Anchor>
      <DNAPopover.Content>
        <DNAText colorMode="dark">{toggleMeetingTypeTooltipMessage}</DNAText>
      </DNAPopover.Content>
    </DNAPopover>
  );

  const focusPopoutWindowButton = (
    <DNAPopover
      placement="top"
      type="tooltip"
      offset={30}
      disablePopover={['tabletPWA']}
    >
      <DNAPopover.Anchor>
        <DNAButton
          {...buttonDefaults}
          testID="focus-popout-window"
          iconLeft="book-open-text"
          onPress={focusPopoutWindow}
        />
      </DNAPopover.Anchor>
      <DNAPopover.Content>
        <DNAText colorMode="dark">View presenter window</DNAText>
      </DNAPopover.Content>
    </DNAPopover>
  );

  return {
    presentationNav,
    myContentButton,
    presentationsButton,
    slidesButton,
    meetingDetailsButton,
    highlighterContextMenu,
    textSearchButton,
    sentimentsContextMenu,
    myNotesButton,
    endMeetingButton,
    changeToVirtualModeButton,
    focusPopoutWindowButton,
  }
}

export default useVirtualnActionBarComponents
